<template>
  <div>
    <v-row>
      <v-col cols="12"><h1 class="font-weight-thin">Organization Settings</h1></v-col>
    </v-row>
    <div class="elevation-0">
      <v-app-bar flat color="gray" dense>
        <v-toolbar-title class="text-h6 pl-0 font-weight-thin">{{ serviceAreaRecord.name }} </v-toolbar-title>
        <v-spacer></v-spacer>
        <span class="text-h6 pl-0 font-weight-thin"> Service Area ID: {{ serviceAreaId }} </span>
      </v-app-bar>
      <v-row v-if="!isPageReady" justify="center">
        <v-col sm="12" lg="5">
          <v-card class="elevation-0">
            <v-card-text>
              <generic-loader caption="Loading organization settings..." topMargin="2"></generic-loader>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <span v-else>
        <v-row align="center" no-gutters>
          <v-col sm="12">
            <v-card class="elevation-0 mt-5" outlined>
              <v-card-title>
                <h2 class="font-weight-thin mb-2">Contacts</h2>
                <v-spacer></v-spacer>
                <v-btn
                  @click="showEditContactsForm = true"
                  color="primary"
                  data-cy="edit-contacts-button"
                  outlined
                  small
                  >Edit Contacts</v-btn
                >
              </v-card-title>
              <v-card-text>
                <v-row class="ml-n8">
                  <v-col>
                    <contact-card label="Primary Contact" :user="serviceAreaRecord.contactPrimary"></contact-card>
                  </v-col>
                  <v-col>
                    <contact-card label="Secondary Contact" :user="serviceAreaRecord.contactSecondary"></contact-card>
                  </v-col>
                  <v-col>
                    <contact-card label="Account Manager" :user="serviceAreaRecord.accountManager"></contact-card>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <div>
                      <strong>Continuum Administrators: </strong>
                      <span data-cy="administrator-list">
                        {{ continuumAdministratorListDisplayText || "None assigned" }}
                      </span>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row class="mt-n5">
          <v-col sm="12">
            <v-card class="elevation-0 mt-5" outlined>
              <v-card-title>
                <h2 class="font-weight-thin mb-2">Application Administration</h2>
                <v-spacer></v-spacer>
                <v-btn
                  @click="navigateToApplicationAdmin"
                  color="primary"
                  data-cy="edit-application-list"
                  outlined
                  small
                  >Edit Settings</v-btn
                >
              </v-card-title>
              <v-card-text>
                <div v-if="!applicationAdminLastUpdatedDate" class="font-weight-bold font-italic red--text">
                  INITIAL APPLICATION SETTINGS CONFIRMATION NEEDED
                </div>
                <div v-else>
                  <p class="mt-2">
                    My organization has indicated use with the following applications as part of our OCHIN Epic
                    configuration.
                  </p>

                  <v-card v-if="isLoadingApplications" class="elevation-0 my-5 pa-5" outlined>
                    <generic-loader caption="Loading applications..." topMargin="0"></generic-loader>
                  </v-card>
                  <v-card v-else class="elevation-0 my-5 pa-5" outlined>
                    <span class="font-weight-bold" data-cy="applications-impacting-members">{{
                      applicationsImpactingMembers
                    }}</span>
                  </v-card>
                </div>
                <div class="mt-2">
                  <div v-if="!applicationAdminLastUpdatedDate" class="font-weight-bold font-italic red--text">
                    NEEDS REVIEW
                  </div>
                  <div v-else class="mt-2">
                    <span class="font-weight-bold">Last updated: </span>
                    <span> {{ applicationAdminLastUpdatedDate || "" }}</span>
                  </div>
                </div>

                <v-checkbox
                  :disabled="isLoadingApplications"
                  :false-value="0"
                  :true-value="1"
                  class="mt-5"
                  data-cy="showNotImpacted-checkbox"
                  dense
                  id="showNotImpacted-checkbox"
                  v-model="showApplicationsNotImpactingMember"
                >
                  <template v-slot:label>
                    <span class="checkbox-label">Show applications not impacting my organization</span>
                  </template>
                </v-checkbox>
                <v-card class="elevation-0 pa-5" outlined v-if="showApplicationsNotImpactingMember">
                  <span class="font-weight-bold" v-if="applicationsNotImpactingMembers.length > 0">{{
                    applicationsNotImpactingMembers
                  }}</span>
                  <span class="font-weight-bold" v-else
                    >All applications have been indicated as impacting my organization.</span
                  >
                  <span></span>
                </v-card>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row class="mt-n5" v-if="historicallyNotImpactedFeatureFlag">
          <v-col sm="12">
            <v-card class="elevation-0 mt-5" outlined>
              <v-card-title>
                <h2 class="font-weight-thin mb-2">Go Live Date</h2>
                <v-spacer></v-spacer>
                <v-btn
                  @click="showEditGoLiveDateForm = true"
                  color="primary"
                  data-cy="editGoLiveDateButton"
                  outlined
                  small
                  >Edit Go Live Date</v-btn
                >
              </v-card-title>
              <v-card-text>
                <div class="mt-2">
                  <strong>Go Live Date: </strong>
                  <span :class="serviceAreaRecord.goLiveDate ? '' : 'font-weight-bold font-italic red--text'">
                    {{ formatGoLiveDate(serviceAreaRecord.goLiveDate) || "REQUIRED" }}
                  </span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row class="mt-n5" v-if="historicallyNotImpactedFeatureFlag">
          <v-col sm="12">
            <v-card class="elevation-0 mt-5" outlined>
              <v-card-title>
                <h2 class="font-weight-thin mb-2">Historical Testing Statuses</h2>
                <v-spacer></v-spacer>
                <v-btn
                  @click="showHistoricalTestingStatusForm = true"
                  color="primary"
                  data-cy="editHistoricalTestingStatusButton"
                  outlined
                  small
                  >Edit Testing Statuses</v-btn
                >
              </v-card-title>
              <v-card-text>
                <div class="mt-2">
                  If a Go Live Date has been set, the testing status for every release note from before this date can be
                  set to <span class="font-italic">Historically Not Impacted</span>. This will adjust UAT completion
                  counts in the UAT Dashboard and also hide these release notes from the default search results. (Note:
                  These release notes can be revealed by unchecking the "Hide Not Impacted" checkbox in the Search
                  feature.)
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </span>
    </div>

    <edit-contacts-dialog v-model="showEditContactsForm" :serviceAreaRecord="serviceAreaRecord"></edit-contacts-dialog>
    <edit-go-live-date-dialog
      v-model="showEditGoLiveDateForm"
      :serviceAreaRecord="serviceAreaRecord"
    ></edit-go-live-date-dialog>
    <edit-testing-status-dialog
      v-model="showHistoricalTestingStatusForm"
      :serviceAreaRecord="serviceAreaRecord"
    ></edit-testing-status-dialog>
  </div>
</template>

<script>
import { formatDateAsMMDDYYYY } from "@/lib/formatters";
import { isEmpty } from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import ContactCard from "./ContactCard";
import EditContactsDialog from "./EditContactsDialog";
import EditGoLiveDateDialog from "./EditGoLiveDateDialog";
import EditTestingStatusDialog from "./EditTestingStatusDialog";
import GenericLoader from "@/components/GenericLoader";

export default {
  components: {
    ContactCard,
    EditContactsDialog,
    EditGoLiveDateDialog,
    EditTestingStatusDialog,
    GenericLoader,
  },
  computed: {
    continuumAdministratorListDisplayText() {
      // Use member admin data from service area record
      return (
        this.serviceAreaRecord?.memberAdminList?.active?.map(user => `${user.firstName} ${user.lastName}`).join(", ") ||
        ""
      );
    },
    excludedApplicationList() {
      return this.serviceAreaRecord.excludedApplicationList || [];
    },
    excludedApplicationSet() {
      return new Set(this.excludedApplicationList);
    },
    applicationsImpactingMembers() {
      if (isEmpty(this.activeApplicationList)) return "";

      const excludedSet = this.excludedApplicationSet;
      return this.activeApplicationList
        .filter(app => !excludedSet.has(app.id))
        .map(app => app.label)
        .join(", ");
    },
    applicationsNotImpactingMembers() {
      if (isEmpty(this.activeApplicationList)) return "";

      const excludedSet = this.excludedApplicationSet;
      return this.activeApplicationList
        .filter(app => excludedSet.has(app.id))
        .map(app => app.label)
        .join(", ");
    },
    historicallyNotImpactedFeatureFlag() {
      return this.featureFlagMap.historicallyNotImpacted;
    },
    ...mapGetters({
      activeApplicationList: "admin/dropdown/getActiveApplicationList",
      applicationAdminLastUpdatedDate: "auditLog/getApplicationAdminLastUpdatedDate",
      excludedApplicationListLastUpdate: "auditLog/getExcludedApplicationListLastUpdate",
      featureFlagMap: "featureFlag/getFlagMap",
    }),
    ...mapState({
      adminDropdownList: state => state.admin.dropdown.list,
      auditLogList: state => state.auditLog.serviceArea,
      isLoadingDropdownList: state => state.admin.dropdown.isLoadingDropdownList,
      historicalTestPlanStatusResult: state => state.testPlan.historicalTestPlanStatusResult,
      serviceAreaId: state => state.session.serviceArea,
      serviceAreaRecord: state => state.admin.serviceArea.record,
    }),
  },
  async created() {
    try {
      // Load fast data first (< 1 second)
      await Promise.all([
        this.fetchServiceArea(this.serviceAreaId),
        this.fetchLastApplicationAdminUpdate(this.serviceAreaId),
      ]);

      // Show page immediately
      this.$nextTick(() => {
        this.isPageReady = true;
      });

      // Load applications in background (optimized)
      try {
        await this.fetchApplicationDropdownOnly();
      } catch (e) {
        console.error("Failed to load applications:", e);
        this.$notifyError({ message: "Error loading applications." });
      } finally {
        this.isLoadingApplications = false;
      }
    } catch (e) {
      console.error(e);
      this.isPageReady = true;
      this.$notifyError({ message: "Error fetching organization data.", timeout: false });
    }
  },
  data() {
    return {
      isPageReady: false,
      isLoadingApplications: true,
      showApplicationsNotImpactingMember: false,
      showEditContactsForm: false,
      showEditGoLiveDateForm: false,
      showHistoricalTestingStatusForm: false,
    };
  },
  methods: {
    formatGoLiveDate(date) {
      if (!date) return "";
      return formatDateAsMMDDYYYY(date);
    },
    navigateToApplicationAdmin() {
      this.$router.push({ name: "adminApplication" });
    },
    ...mapActions({
      fetchAdminDropdownList: "admin/dropdown/list",
      fetchApplicationDropdownOnly: "admin/dropdown/listApplications",
      fetchServiceArea: "admin/serviceArea/getServiceArea",
      fetchLastApplicationAdminUpdate: "auditLog/fetchLastApplicationAdminUpdate",
      patchUpdateServiceArea: "admin/serviceArea/patchUpdate",
    }),
  },
};
</script>

<style>
.checkbox-label {
  font-size: 0.875rem; /* 14px */
  color: black;
}
</style>
